export interface SttCredentials {
    token: string
    model_id: string
    realtime_ws_origin: string
}

export async function fetchSttCredentials(): Promise<SttCredentials> {
    const response = await fetch('/api/text_to_speech/stt_token', {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
    })
    const json = await response.json()
    if (!json.success) {
        throw new Error(json.error || 'Failed to get transcription token')
    }
    return json.data as SttCredentials
}

function buildRealtimeUrl(creds: SttCredentials): string {
    const base = creds.realtime_ws_origin.replace(/\/$/, '')
    const q = new URLSearchParams({
        model_id: creds.model_id,
        token: creds.token,
    })
    return `${base}/v1/speech-to-text/realtime?${q.toString()}`
}

export class ElevenLabsScribeSession {
    private ws: WebSocket | null = null
    private committedText = ''
    private sessionStarted = false
    private readonly onPartialCb: (text: string) => void
    private readonly onInputErrorCb: (message: string) => void
    private bumpCommittedSettleTimer: (() => void) | null = null

    constructor(callbacks: {
        onPartial: (text: string) => void
        onInputError: (message: string) => void
    }) {
        this.onPartialCb = callbacks.onPartial
        this.onInputErrorCb = callbacks.onInputError
    }

    async connect(creds: SttCredentials): Promise<void> {
        const url = buildRealtimeUrl(creds)
        return new Promise((resolve, reject) => {
            const ws = new WebSocket(url)
            this.ws = ws
            let settled = false

            const fail = (err: Error) => {
                if (!settled) {
                    settled = true
                    ws.close()
                    reject(err)
                }
            }

            const t = window.setTimeout(() => {
                fail(new Error('Transcription session timed out'))
            }, 20000)

            const doneStart = () => {
                if (!settled) {
                    settled = true
                    window.clearTimeout(t)
                    resolve()
                }
            }

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data as string)
                    switch (data.message_type) {
                        case 'session_started':
                            this.sessionStarted = true
                            doneStart()
                            break
                        case 'partial_transcript':
                            if (data.text && this.sessionStarted) {
                                this.onPartialCb(this.allPreviewText(data.text))
                            }
                            break
                        case 'committed_transcript':
                            if (data.text && this.sessionStarted) {
                                this.committedText = [this.committedText, data.text].filter(Boolean).join(' ')
                                this.onPartialCb(this.committedText.trim())
                                this.bumpCommittedSettleTimer?.()
                            }
                            break
                        case 'committed_transcript_with_timestamps':
                            if (data.text && this.sessionStarted) {
                                this.committedText = [this.committedText, data.text].filter(Boolean).join(' ')
                                this.onPartialCb(this.committedText.trim())
                                this.bumpCommittedSettleTimer?.()
                            }
                            break
                        case 'input_error':
                            this.onInputErrorCb(JSON.stringify(data))
                            break
                        default:
                            break
                    }
                } catch {
                    /* ignore */
                }
            }

            ws.onerror = () => {
                fail(new Error('WebSocket connection failed'))
            }

            ws.onclose = () => {
                if (!settled) {
                    fail(new Error('WebSocket closed before session started'))
                }
            }
        })
    }

    private allPreviewText(partial: string): string {
        const base = this.committedText.trim()
        return base ? `${base} ${partial}` : partial
    }

    sendPcmChunk(audioBase64: string, commit: boolean): void {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            return
        }
        this.ws.send(
            JSON.stringify({
                message_type: 'input_audio_chunk',
                audio_base_64: audioBase64,
                commit,
                sample_rate: 16000,
            })
        )
    }

    commitEnd(): void {
        this.sendPcmChunk('', true)
    }

    /**
     * Sends commit, keeps the websocket open until committed segments settle or `maxMs` elapses.
     * Use after stopping audio capture so trailing commits can arrive before `close`.
     */
    commitEndAwaitSettled(options?: { settleMs?: number; maxMs?: number }): Promise<string> {
        const settleMs = options?.settleMs ?? 350
        const maxMs = options?.maxMs ?? 2500

        let quietTimer: number | undefined
        let deadlineTimer: number | undefined
        let finished = false

        const cleanup = () => {
            if (quietTimer !== undefined) {
                window.clearTimeout(quietTimer)
                quietTimer = undefined
            }
            if (deadlineTimer !== undefined) {
                window.clearTimeout(deadlineTimer)
                deadlineTimer = undefined
            }
            this.bumpCommittedSettleTimer = null
        }

        let resolveOuter!: (text: string) => void
        const promise = new Promise<string>((resolve) => {
            resolveOuter = resolve
        })

        const finish = () => {
            if (finished) {
                return
            }
            finished = true
            cleanup()
            resolveOuter(this.getFinalText())
        }

        this.bumpCommittedSettleTimer = () => {
            if (quietTimer !== undefined) {
                window.clearTimeout(quietTimer)
            }
            quietTimer = window.setTimeout(finish, settleMs)
        }

        deadlineTimer = window.setTimeout(finish, maxMs)
        this.sendPcmChunk('', true)

        return promise
    }

    getFinalText(): string {
        return this.committedText.trim()
    }

    close(): void {
        if (this.ws) {
            this.ws.close()
            this.ws = null
        }
    }
}
